// ============================================================================
// AFB-0075 — the arithmetic and the refusals behind placing a field on a
// template's own PDF, by hand.
//
// Why this file exists at all. The AcroForm importer that shipped 2026-08-20
// reads the boxes a PDF declares about itself, and for the two documents that
// matter most in PKT-0001 it finds nothing: the Employee handbook (15 pages,
// checklist key handbook_signed) and the Employee personal information form
// declare ZERO widgets — no /AcroForm dictionary, no annotations. Measured
// 2026-08-25 against the stored bytes. So for those two, "place fields from
// this PDF's form" can never produce a single box, and the only route to a
// signable handbook is a person putting the boxes on the page.
//
// Why the rules are HERE and not in the component. envelope-lib.ts imports
// pdfjs-dist at load time, which needs DOMMatrix, so anything importing it
// cannot be loaded by a node test at all — the same reason template-bindings,
// template-document, packet-composition and acroform-placement are separate
// files. Only `import type` crosses that line below. Per envelope-lib's own
// boundary rule: if a decision could be got wrong, it belongs on this side.
//
// THE COORDINATE CONTRACT IS NOT RESTATED HERE — it is the one in
// ./acroform-placement: fractions of the page in [0,1], TOP-LEFT origin, the
// stored pair being the box's top-left corner, width and height normalised
// against page width and page height separately, 4dp. The drop and move
// arithmetic below is deliberately identical to envelope-prep.tsx's onDragEnd
// (centre the box on the cursor, clamp to 1 - size, round to 4dp) so that a box
// placed on a template and a box placed on an envelope are the same box. If you
// change one, change both, or a layout captured from an envelope will not line
// up with one authored on a template.
// ============================================================================

import type { TemplateField, TemplateRecipientRole } from './envelope-lib';

/**
 * Size clamps, matching envelope-prep.tsx's resizeSelected exactly.
 *
 * The lower bounds are what the signer surface can still be given a legible
 * hit target for; the upper bounds stop a stray drag turning one field into a
 * box covering the whole page, which reads as the page being broken rather
 * than as a mistake somebody made.
 */
export const MIN_FIELD_W = 0.02;
export const MAX_FIELD_W = 0.9;
export const MIN_FIELD_H = 0.012;
export const MAX_FIELD_H = 0.3;

/** The engine's default when a layout entry names no role. */
export const DEFAULT_ROLE_KEY = 'employee';

/** Just the geometry of a placed box, in stored form. */
export interface PlacedBox {
  position_x: number;
  position_y: number;
  width: number;
  height: number;
}

/** Rounded the way onDragEnd rounds, so a dragged box and a resized one match. */
export function round4(v: number): number {
  return Number(v.toFixed(4));
}

function clamp01(v: number): number {
  return Math.min(Math.max(v, 0), 1);
}

/**
 * Where a page is on screen and how big it is, in CSS pixels.
 *
 * Deliberately a plain shape rather than a DOMRect so this is callable from a
 * node test. The caller passes getBoundingClientRect()'s left/top and the
 * RenderedPage's width/height — NOT offsetWidth/offsetHeight, which include
 * the 1px border the page wrapper draws and would shift every box by a
 * fraction of a point.
 */
export interface PageFrame {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * A point in viewport coordinates becomes the CENTRE of a box of size w × h.
 *
 * Centre-anchored, not corner-anchored, because the thing being dragged is a
 * chip the size of the box: anchoring the corner would drop every field half a
 * box down and to the right of where the person let go.
 */
export function boxAtPoint(
  pointX: number,
  pointY: number,
  page: PageFrame,
  w: number,
  h: number,
): PlacedBox {
  const x = Math.min(Math.max((pointX - page.left) / page.width - w / 2, 0), 1 - w);
  const y = Math.min(Math.max((pointY - page.top) / page.height - h / 2, 0), 1 - h);
  return {
    position_x: round4(x),
    position_y: round4(y),
    width: round4(w),
    height: round4(h),
  };
}

/** Move an existing box so its centre lands on a point. Size is unchanged. */
export function moveBoxToPoint(
  box: PlacedBox,
  pointX: number,
  pointY: number,
  page: PageFrame,
): PlacedBox {
  return boxAtPoint(pointX, pointY, page, box.width, box.height);
}

export type ResizeHandle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

/**
 * Drag one handle by a fractional delta.
 *
 * The edges the handle does not own STAY PUT. That is the whole behaviour worth
 * testing: dragging the west handle must not move the right-hand edge, because
 * a signature box whose right edge creeps left every time somebody widens it
 * from the other side ends up off the ruled line, and nothing on screen says so.
 *
 * Order matters — page edges first, then the minimum, then the maximum — so a
 * drag that goes past the paper produces the largest legal box against that
 * edge rather than an inverted one.
 */
export function resizeBox(
  box: PlacedBox,
  handle: ResizeHandle,
  dx: number,
  dy: number,
): PlacedBox {
  const movesLeft = handle === 'nw' || handle === 'w' || handle === 'sw';
  const movesRight = handle === 'ne' || handle === 'e' || handle === 'se';
  const movesTop = handle === 'nw' || handle === 'n' || handle === 'ne';
  const movesBottom = handle === 'sw' || handle === 's' || handle === 'se';

  let left = box.position_x;
  let right = box.position_x + box.width;
  let top = box.position_y;
  let bottom = box.position_y + box.height;

  if (movesLeft) left = clamp01(left + dx);
  if (movesRight) right = clamp01(right + dx);
  if (movesTop) top = clamp01(top + dy);
  if (movesBottom) bottom = clamp01(bottom + dy);

  if (right - left < MIN_FIELD_W) {
    if (movesLeft) left = Math.max(0, right - MIN_FIELD_W);
    else right = Math.min(1, left + MIN_FIELD_W);
  }
  if (right - left > MAX_FIELD_W) {
    if (movesLeft) left = right - MAX_FIELD_W;
    else right = left + MAX_FIELD_W;
  }
  if (bottom - top < MIN_FIELD_H) {
    if (movesTop) top = Math.max(0, bottom - MIN_FIELD_H);
    else bottom = Math.min(1, top + MIN_FIELD_H);
  }
  if (bottom - top > MAX_FIELD_H) {
    if (movesTop) top = bottom - MAX_FIELD_H;
    else bottom = top + MAX_FIELD_H;
  }

  return {
    position_x: round4(left),
    position_y: round4(top),
    width: round4(right - left),
    height: round4(bottom - top),
  };
}

// ── the three refusals nothing enforced before ──────────────────────────────
//
// templatePacketBlockers already covers "no document", "no page count" and
// every unresolved merge binding. It does NOT check that a field is on a page
// the document has, that the box is on the paper, or that the role it points at
// exists. All three were representable and silently unnoticed:
//
//   · A layout captured from a five-page envelope and saved onto a three-page
//     template keeps its page numbers. saveLayoutToTemplate is a raw update.
//   · Renaming or deleting a role orphans every field pointing at the old key.
//     esign_instantiate_packet_envelope reports it as an `unresolved` entry at
//     SEND time — after the envelope row already exists — and the authoring
//     screen said nothing at all.
//   · Nothing validates geometry on the save path. position_x: 1.4 persists.
//
// Each sentence completes "Field 3 …", matching fieldTaggingGap's phrasing so
// the two lists read as one list.

/** What a layout has to be checked against. */
export interface PlacementContext {
  page_count: number | null;
  recipient_roles: TemplateRecipientRole[] | null;
}

function roleKeysOf(ctx: PlacementContext): string[] {
  return Array.isArray(ctx.recipient_roles)
    ? ctx.recipient_roles.map((r) => (r?.role_key ?? '').trim()).filter((k) => k !== '')
    : [];
}

function finite(v: unknown): boolean {
  return typeof v === 'number' && Number.isFinite(v);
}

/**
 * Why this one entry could not be placed, or null when it can.
 *
 * Geometry is checked before the page, and the page before the role, so the
 * first sentence a person reads is the one that is easiest to act on.
 */
export function fieldPlacementGap(
  field: Partial<TemplateField> | null | undefined,
  ctx: PlacementContext,
): string | null {
  if (!field) return 'is empty.';

  const { position_x: x, position_y: y, width: w, height: h } = field;
  if (!finite(x) || !finite(y) || !finite(w) || !finite(h)) {
    return 'has no position on the page.';
  }
  if ((w as number) <= 0 || (h as number) <= 0) {
    return 'has no size, so there would be nothing to click.';
  }
  if (
    (x as number) < 0
    || (y as number) < 0
    || (x as number) + (w as number) > 1
    || (y as number) + (h as number) > 1
  ) {
    return 'extends past the edge of the page — drag it back onto the paper.';
  }

  const page = field.page;
  if (!finite(page) || !Number.isInteger(page) || (page as number) < 1) {
    return 'is not on a page.';
  }
  const pages = ctx.page_count ?? 0;
  if (pages > 0 && (page as number) > pages) {
    return (
      `is on page ${page} of a ${pages}-page document, so it could not be placed. `
      + 'It was probably captured from a longer envelope.'
    );
  }

  const keys = roleKeysOf(ctx);
  const wanted = (field.role_key ?? '').trim() || DEFAULT_ROLE_KEY;
  if (!keys.includes(wanted)) {
    return keys.length === 0
      ? `belongs to "${wanted}", but nobody is set to sign this document yet.`
      : `belongs to "${wanted}", which is not one of this document's roles `
        + `(${keys.join(', ')}) — nobody would be asked to fill it.`;
  }

  return null;
}

/** Every entry that could not be placed, with its index. */
export function unplaceableFields(
  layout: Array<Partial<TemplateField>> | null | undefined,
  ctx: PlacementContext,
): Array<{ index: number; reason: string }> {
  if (!Array.isArray(layout)) return [];
  const out: Array<{ index: number; reason: string }> = [];
  layout.forEach((field, index) => {
    const reason = fieldPlacementGap(field, ctx);
    if (reason) out.push({ index, reason });
  });
  return out;
}

/** The same list as sentences, ready to sit beside templatePacketBlockers'. */
export function placementBlockers(
  layout: Array<Partial<TemplateField>> | null | undefined,
  ctx: PlacementContext,
): string[] {
  return unplaceableFields(layout, ctx).map(
    ({ index, reason }) => `Field ${index + 1} ${reason}`,
  );
}

/**
 * How many fields sit on each page, for every page the document has.
 *
 * Pages with none are INCLUDED, at zero. A page missing from this list would
 * be a page the author cannot tell apart from one they have already done, and
 * the whole job here is knowing which pages are still blank.
 */
export function fieldsPerPage(
  layout: Array<Partial<TemplateField>> | null | undefined,
  pageCount: number | null,
): Array<{ page: number; count: number }> {
  const pages = pageCount ?? 0;
  const out: Array<{ page: number; count: number }> = [];
  for (let p = 1; p <= pages; p += 1) out.push({ page: p, count: 0 });
  if (!Array.isArray(layout)) return out;
  for (const f of layout) {
    const p = f?.page;
    if (!finite(p) || !Number.isInteger(p)) continue;
    const hit = out.find((o) => o.page === p);
    if (hit) hit.count += 1;
  }
  return out;
}

/**
 * Does this template ask anyone to sign it? Mirrors the predicate in
 * esign_packet_plan's field_layout gap — deliberately the predicate and not the
 * sentence. The database owns the refusal a person reads when a packet is
 * blocked; this only decides whether the authoring screen nudges. A courtesy
 * check that disagrees with the server rule is its own kind of dead end.
 */
export function declaresASigner(ctx: PlacementContext): boolean {
  return Array.isArray(ctx.recipient_roles)
    && ctx.recipient_roles.some((r) => (r?.role ?? 'SIGNER').toUpperCase() === 'SIGNER');
}
